import React, { useEffect, useRef, useState } from "react";
import {
  createStaffUser,
  createDeviceSession,
  deleteStaffUser,
  fetchStaff,
  lockToPinScreen,
  resetStaffPin,
  revokeDevices,
  resolveImageUrl,
  updateStaffUser,
  hasFeature,
  uploadStaffPhoto,
  getVenueType,
} from "../services/api";
import { useT } from "../i18n/useT";

// Which staff roles make sense for each venue type. A restaurant has no
// front-desk/housekeeping or wellness roles; a spa/gym has no F&B or
// housekeeping roles. A hotel is the superset.
const VENUE = (typeof getVenueType === "function" ? getVenueType() : "hotel");
const ROLES_FNB = VENUE === "hotel" || VENUE === "restaurant";        // waiter, chef
const ROLES_FRONTDESK = VENUE === "hotel" || VENUE === "spa_gym";     // front desk (reception)
const ROLES_HOUSEKEEPING = VENUE === "hotel";                        // housekeeper, cleaner
const ROLES_WELLNESS = VENUE === "hotel" || VENUE === "spa_gym";     // therapist … lifeguard

const EMPTY = { email: "", password: "", role: ROLES_FNB ? "waiter" : "cashier", name: "", code: "" };
// SEC-2 data-access permissions a manager can grant/restrict per staff member.
const PERMS = [
  ["view_financials", "Revenue & financial reports"],
  ["view_food_cost", "F&B cost control"],
  ["view_reports", "F&B analytics"],
  ["export_data", "Export data (CSV)"],
];

// Turn any API error into a readable string. FastAPI 422 details are a LIST of
// objects; rendering that directly crashes React (white screen). This makes
// every error safe to show and meaningful where possible.
function toMsg(err, fallback) {
  const d = err && err.response && err.response.data && err.response.data.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return d[0].msg || fallback;
  if (d && typeof d === "object") return d.message || d.msg || fallback;
  return fallback;
}

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    heading: "Staff",
    add_photo: "Add photo",
    change_photo: "Change photo",
    uploading_photo: "Uploading…",
    could_not_load: "Could not load staff.",
    could_not_create: "Could not create staff member.",
    added: "Added {email}.",
    update_failed: "Update failed.",
    delete_failed: "Delete failed.",
    new_password_prompt: "New password for {email}:",
    password_updated: "Password updated.",
    confirm_delete: "Delete {email}? This cannot be undone.",
    add_staff_member: "Add staff member",
    email: "Email",
    name_optional: "Name (optional)",
    role: "Role",
    role_waiter: "Waiter",
    role_chef: "Chef",
    role_manager: "Manager",
    initial_password: "Initial password",
    password_placeholder: "Min 6 characters",
    password_share_hint: "Share this with the staff member; they can ask you to reset it.",
    adding: "Adding...",
    add_staff: "Add staff",
    team: "Team ({n})",
    no_staff_yet: "No staff yet. The owner login still works via the role dropdown at sign-in.",
    th_email: "Email",
    th_name: "Name",
    th_role: "Role",
    th_active: "Active",
    disable: "Disable",
    enable: "Enable",
    reset_password: "Reset password",
    delete: "Delete",
    filter_all: "All",
    search_ph: "Search name, code or email…",
    code_optional: "Code (optional)",
    code_ph: "e.g. HK-03",
    nologin_hint: "This role doesn't sign in — a name (and optional code) is enough. Leave email & password blank.",
    no_login_tag: "no login",
    th_code: "Code",
    th_status: "Status",
    th_actions: "Actions",
    st_active: "Active",
    st_disabled: "Disabled",
    access_menu: "Data access",
    reset_code: "Reset code",
    staff_member: "Staff member",
  },
  am: {
    heading: "ሠራተኞች",
    could_not_load: "ሠራተኞችን መጫን አልተቻለም።",
    could_not_create: "ሠራተኛ መፍጠር አልተቻለም።",
    added: "{email} ታክሏል።",
    update_failed: "ማዘመን አልተሳካም።",
    delete_failed: "ማጥፋት አልተሳካም።",
    new_password_prompt: "ለ{email} አዲስ የይለፍ ቃል፦",
    password_updated: "የይለፍ ቃል ተዘምኗል።",
    confirm_delete: "{email} አጥፋ? ይህ መቀልበስ አይቻልም።",
    add_staff_member: "ሠራተኛ ጨምር",
    email: "ኢሜይል",
    name_optional: "ስም (አማራጭ)",
    role: "ሚና",
    role_waiter: "አስተናጋጅ",
    role_chef: "ሼፍ",
    role_manager: "ሥራ አስኪያጅ",
    initial_password: "የመጀመሪያ የይለፍ ቃል",
    password_placeholder: "ቢያንስ 6 ቁምፊዎች",
    password_share_hint: "ይህን ለሠራተኛው ያጋሩ፤ እንዲቀይሩለት ሊጠይቁዎት ይችላሉ።",
    adding: "በመጨመር ላይ...",
    add_staff: "ሠራተኛ ጨምር",
    team: "ቡድን ({n})",
    no_staff_yet: "እስካሁን ሠራተኛ የለም። የባለቤቱ መግቢያ አሁንም በመግቢያ ላይ ካለው የሚና ዝርዝር በኩል ይሰራል።",
    th_email: "ኢሜይል",
    th_name: "ስም",
    th_role: "ሚና",
    th_active: "ንቁ",
    disable: "አሰናክል",
    enable: "አንቃ",
    reset_password: "የይለፍ ቃል ዳግም አስጀምር",
    delete: "አጥፋ",
    th_status: "ሁኔታ",
    th_actions: "ድርጊቶች",
    st_active: "ንቁ",
    st_disabled: "ተሰናክሏል",
    access_menu: "የመረጃ መዳረሻ",
    reset_code: "ኮድ ዳግም አስጀምር",
    staff_member: "ሠራተኛ",
  },
  om: {
    heading: "Hojjettoota",
    could_not_load: "Hojjettoota fe'uun hin danda'amne.",
    could_not_create: "Hojjetaa uumuun hin danda'amne.",
    added: "{email} dabalameera.",
    update_failed: "Haaromsuun hin milkoofne.",
    delete_failed: "Balleessuun hin milkoofne.",
    new_password_prompt: "Jecha icciitii haaraa {email}f:",
    password_updated: "Jechi icciitii haaromfameera.",
    confirm_delete: "{email} balleessuu? Kun deebi'uu hin danda'u.",
    add_staff_member: "Hojjetaa dabali",
    email: "Imeelii",
    name_optional: "Maqaa (filannoo)",
    role: "Ga'ee",
    role_waiter: "Tajaajilaa",
    role_chef: "Cheef",
    role_manager: "Hoogganaa",
    initial_password: "Jecha icciitii jalqabaa",
    password_placeholder: "Yoo xiqqaate qubee 6",
    password_share_hint: "Kana hojjetaa wajjin qoodi; akka isiniif haaromsitan gaafachuu danda'u.",
    adding: "Dabalaa jira...",
    add_staff: "Hojjetaa dabali",
    team: "Garee ({n})",
    no_staff_yet: "Hojjetaan hin jiru. Seenuun abbaa amma illee tarree ga'ee seensaa keessaan ni hojjeta.",
    th_email: "Imeelii",
    th_name: "Maqaa",
    th_role: "Ga'ee",
    th_active: "Sosso'aa",
    disable: "Dhaamsi",
    enable: "Banii",
    reset_password: "Jecha icciitii haaromsi",
    delete: "Balleessi",
    th_status: "Haala",
    th_actions: "Gochaalee",
    st_active: "Sosso'aa",
    st_disabled: "Dhaamame",
    access_menu: "Daataa argachuu",
    reset_code: "Koodii haaromsi",
    staff_member: "Hojjetaa",
  },
};

export default function ManagerStaffPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [roleFilter, setRoleFilter] = useState("all");
  const [accessId, setAccessId] = useState(null);
  const [menuId, setMenuId] = useState(null);   // staff id whose "⋯" actions menu is open
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  const [formMsg, setFormMsg] = useState(null);   // {ok: bool, text} for the add-staff form
  const [busy, setBusy] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef(null);

  const triggerPhoto = (s) => { setUploadingFor(s.id); fileInputRef.current?.click(); };
  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadingFor) return;
    try { await uploadStaffPhoto(uploadingFor, file); load(); }
    catch (err) { setError(err?.response?.data?.detail || t("update_failed")); }
    finally { setUploadingFor(null); }
  };

  const load = () =>
    fetchStaff()
      .then(setItems)
      .catch((e) => setError(e?.response?.data?.detail || t("could_not_load")));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setFormMsg(null);
    setBusy(true);
    try {
      await createStaffUser({ ...form, email: (form.email || "").trim() || null, password: (form.password || "").trim() || null });
      setFormMsg({ ok: true, text: t("added").replace("{email}", form.email || form.name || form.code || "staff") });
      setForm(EMPTY);
      load();
    } catch (err) {
      setFormMsg({ ok: false, text: toMsg(err, t("could_not_create")) });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await updateStaffUser(s.id, { active: !s.active });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("update_failed"));
    }
  };

  const togglePerm = async (s, perm) => {
    const cur = new Set(s.effective_permissions || []);
    cur.has(perm) ? cur.delete(perm) : cur.add(perm);
    const body = {};
    PERMS.forEach(([k]) => { body[k] = cur.has(k); });
    try { await updateStaffUser(s.id, { permissions: body }); load(); }
    catch (err) { setError(err?.response?.data?.detail || "Could not update access."); }
  };
  const setRole = async (s, role) => {
    try {
      await updateStaffUser(s.id, { role });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("update_failed"));
    }
  };

  const setupDevice = async () => {
    if (!window.confirm("Turn THIS tablet into a shared device? Staff sign in with their 4-character code, and you'll be signed out of your manager account on this tablet.")) return;
    try { await createDeviceSession(); lockToPinScreen(); window.location.href = "/lock"; }
    catch (err) { setError(err?.response?.data?.detail || "Could not set up the shared device."); }
  };

  const signOutDevices = async () => {
    if (!window.confirm("Sign out ALL shared tablets for this venue (e.g. a lost tablet)? Each will need to be set up again.")) return;
    try { await revokeDevices(); window.alert("All shared devices have been signed out."); }
    catch (err) { setError(err?.response?.data?.detail || "Could not sign out devices."); }
  };

  const resetCode = async (s) => {
    if (!window.confirm("Reset " + s.email + "'s quick code? They will set a new one.")) return;
    try { await resetStaffPin(s.id); window.alert("Code cleared. The staff member can set a new one."); load(); }
    catch (err) { setError(err?.response?.data?.detail || "Could not reset the code."); }
  };

  const stationsOn = hasFeature("stations");
  const costControlOn = hasFeature("cost_control");
  const backOfficeOn = hasFeature("back_office");
  const STATION_OPTS = [["kitchen", "🍳"], ["bar", "🍺"], ["cafe", "☕"]];
  const toggleStation = async (s, key) => {
    const cur = (s.stations || "").split(",").filter(Boolean);
    const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key];
    try { await updateStaffUser(s.id, { stations: next.join(",") }); load(); }
    catch (err) { setError(err?.response?.data?.detail || "Could not update stations."); }
  };

  const resetPassword = async (s) => {
    const pw = window.prompt(t("new_password_prompt").replace("{email}", s.email));
    if (!pw) return;
    try {
      await updateStaffUser(s.id, { password: pw });
      window.alert(t("password_updated"));
    } catch (err) {
      setError(err?.response?.data?.detail || t("update_failed"));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(t("confirm_delete").replace("{email}", s.email))) return;
    try {
      await deleteStaffUser(s.id);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("delete_failed"));
    }
  };

  const ROLE_LABELS = {
    manager: t("role_manager"), chef: t("role_chef"), waiter: t("role_waiter"),
    controller: "Controller", store_manager: "Store Manager", cashier: "Cashier", front_desk: "Front Desk",
    housekeeper: "Housekeeper", cleaner: "Cleaner",
    therapist: "Therapist", trainer: "Trainer", hairdresser: "Hairdresser",
    beautician: "Beautician", lifeguard: "Lifeguard",
  };
  const roleLabel = (r) => ROLE_LABELS[r] || r;
  // Roles actually present, in a stable, readable order.
  const ROLE_ORDER = ["manager", "front_desk", "controller", "store_manager", "cashier", "chef", "waiter", "housekeeper", "cleaner", "therapist", "trainer", "hairdresser", "beautician", "lifeguard"];
  // Operational roles that can be created without a login (no email/password).
  const NO_LOGIN = ["cleaner", "therapist", "trainer", "hairdresser", "beautician", "lifeguard"];
  const presentRoles = ROLE_ORDER.filter((r) => items.some((s) => s.role === r));
  const q = query.trim().toLowerCase();
  const filtered = items.filter((s) =>
    (roleFilter === "all" || s.role === roleFilter) &&
    (!q || [s.name, s.code, s.no_login ? "" : s.email].some((v) => (v || "").toLowerCase().includes(q)))
  );

  // One row in the "⋯" actions menu — runs the action and closes the menu.
  const mItem = (label, action, danger) => (
    <button
      onClick={() => { setMenuId(null); action(); }}
      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 13.5,
        color: danger ? "#A32D2D" : "#1d2b32" }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? "#FCEBEB" : "#f2f9f8"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
    >
      {label}
    </button>
  );

  return (
    <>
      <h1>{t("heading")}</h1>
      {error && <div className="error">{typeof error === "string" ? error : t("could_not_create")}</div>}

      <div className="card" style={{ marginBottom: 16, borderLeft: "4px solid #008080" }}>
        <h3 style={{ marginTop: 0 }}>📱 Shared tablet (quick codes)</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13, maxWidth: 640 }}>
          Set up one tablet that several {ROLES_FNB ? "waiters/chefs" : "team members"} share — each taps their own 4-character code to start their shift. Staff set their code under "My code"; reset one below if forgotten.
        </p>
        <button className="btn" onClick={setupDevice}>Set up THIS tablet as a shared device</button>{" "}
        <button className="btn secondary" onClick={signOutDevices}>Sign out all devices</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(340px, 1fr) 1.6fr", gap: 20 }}>
        <form className="card" onSubmit={submit}>
          <h3 style={{ marginTop: 0 }}>{t("add_staff_member")}</h3>
          {!NO_LOGIN.includes(form.role) && (
          <div className="field">
            <label>{t("email")}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required={!NO_LOGIN.includes(form.role)}
            />
          </div>
          )}
          <div className="field">
            <label>{t("name_optional")}</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="field">
            <label>{t("code_optional")}</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder={t("code_ph")}
            />
          </div>
          <div className="field">
            <label>{t("role")}</label>
            <select
              value={form.role}
              onChange={(e) => { const r = e.target.value; setForm({ ...form, role: r, ...(NO_LOGIN.includes(r) ? { email: "", password: "" } : {}) }); }}
            >
              {ROLES_FNB && <option value="waiter">{t("role_waiter")}</option>}
              {ROLES_FNB && <option value="chef">{t("role_chef")}</option>}
              <option value="manager">{t("role_manager")}</option>
              {costControlOn && <option value="controller">Controller</option>}
              {backOfficeOn && <option value="store_manager">Store Manager</option>}
              <option value="cashier">Cashier</option>
              {ROLES_FRONTDESK && <option value="front_desk">Front Desk</option>}
              {ROLES_HOUSEKEEPING && <option value="housekeeper">Housekeeper</option>}
              {ROLES_HOUSEKEEPING && <option value="cleaner">Cleaner</option>}
              {ROLES_WELLNESS && (
                <optgroup label="Leisure & Wellness">
                  <option value="therapist">Therapist</option>
                  <option value="trainer">Trainer</option>
                  <option value="hairdresser">Hairdresser</option>
                  <option value="beautician">Beautician</option>
                  <option value="lifeguard">Lifeguard</option>
                </optgroup>
              )}
            </select>
          </div>
          {NO_LOGIN.includes(form.role) ? (
            <div className="muted" style={{ fontSize: "0.82rem", marginBottom: 10 }}>
              {t("nologin_hint")}
            </div>
          ) : (
            <div className="field">
              <label>{t("initial_password")}</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!NO_LOGIN.includes(form.role)}
                minLength={6}
                placeholder={t("password_placeholder")}
              />
              <div className="muted" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                {t("password_share_hint")}
              </div>
            </div>
          )}
          {formMsg && (
            <div style={{ marginBottom: 10, fontWeight: 600, color: formMsg.ok ? "#1D9E75" : "#A32D2D" }}>
              {formMsg.ok ? "✓ " : "⚠ "}{formMsg.text}
            </div>
          )}
          <button className="btn" type="submit" disabled={busy}>
            {busy ? t("adding") : t("add_staff")}
          </button>
        </form>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>{t("team").replace("{n}", items.length)}</h3>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
                 style={{ display: "none" }} onChange={onPhotoChosen} />

          {items.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", margin: "4px 0 12px" }}>
              <button type="button" onClick={() => setRoleFilter("all")}
                className={"btn small " + (roleFilter === "all" ? "" : "secondary")} style={{ padding: "3px 10px" }}>
                {t("filter_all")} ({items.length})
              </button>
              {presentRoles.map((r) => (
                <button key={r} type="button" onClick={() => setRoleFilter(r)}
                  className={"btn small " + (roleFilter === r ? "" : "secondary")} style={{ padding: "3px 10px" }}>
                  {roleLabel(r)} ({items.filter((s) => s.role === r).length})
                </button>
              ))}
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_ph")}
                style={{ marginLeft: "auto", minWidth: 180, padding: "5px 9px", borderRadius: 8, border: "1px solid #d8dee2", fontSize: 13 }} />
            </div>
          )}

          {items.length === 0 ? (
            <p className="muted">
              {t("no_staff_yet")}
            </p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th colSpan={2}>{t("staff_member")}</th>
                  <th>{t("th_name")}</th>
                  <th>{t("th_code")}</th>
                  <th>{t("th_role")}</th>
                  {stationsOn && <th>Stations</th>}
                  <th>{t("th_status")}</th>
                  <th style={{ textAlign: "right" }}>{t("th_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <React.Fragment key={s.id}>
                  <tr style={{ opacity: s.active ? 1 : 0.55 }}>
                    <td style={{ width: 54 }}>
                      {s.photo_url ? (
                        <img src={resolveImageUrl(s.photo_url)} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: 42, height: 42, borderRadius: "50%", background: "#e6eaee", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7484", fontWeight: 700 }}>
                          {(s.name || s.email || "?").trim().charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td>{s.no_login
                      ? <span className="muted" style={{ fontSize: 12, fontStyle: "italic" }}>{t("no_login_tag")}</span>
                      : <b>{s.email}</b>}</td>
                    <td className="muted">{s.name || "—"}</td>
                    <td className="muted">{s.code || "—"}</td>
                    <td>
                      <select
                        value={s.role}
                        onChange={(e) => setRole(s, e.target.value)}
                      >
                        {(ROLES_FNB || s.role === "waiter") && <option value="waiter">{t("role_waiter")}</option>}
                        {(ROLES_FNB || s.role === "chef") && <option value="chef">{t("role_chef")}</option>}
                        <option value="manager">{t("role_manager")}</option>
                        {costControlOn && <option value="controller">Controller</option>}
                        {(backOfficeOn || s.role === "store_manager") && <option value="store_manager">Store Manager</option>}
                        <option value="cashier">Cashier</option>
                        {(ROLES_FRONTDESK || s.role === "front_desk") && <option value="front_desk">Front Desk</option>}
                        {(ROLES_HOUSEKEEPING || s.role === "housekeeper") && <option value="housekeeper">Housekeeper</option>}
                        {(ROLES_HOUSEKEEPING || s.role === "cleaner") && <option value="cleaner">Cleaner</option>}
                        {(ROLES_WELLNESS || ["therapist", "trainer", "hairdresser", "beautician", "lifeguard"].includes(s.role)) && (
                          <optgroup label="Leisure & Wellness">
                            <option value="therapist">Therapist</option>
                            <option value="trainer">Trainer</option>
                            <option value="hairdresser">Hairdresser</option>
                            <option value="beautician">Beautician</option>
                            <option value="lifeguard">Lifeguard</option>
                          </optgroup>
                        )}
                      </select>
                    </td>
                    {stationsOn && (
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          {STATION_OPTS.map(([k, icon]) => {
                            const on = (s.stations || "").split(",").includes(k);
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => toggleStation(s, k)}
                                title={k}
                                className={"btn small " + (on ? "" : "secondary")}
                                style={{ padding: "2px 8px" }}
                              >
                                {icon}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    )}
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 10,
                        background: s.active ? "#e7f6ec" : "#f1efe8", color: s.active ? "#1D7A44" : "#5F5E5A" }}>
                        {uploadingFor === s.id ? t("uploading_photo") : (s.active ? t("st_active") : t("st_disabled"))}
                      </span>
                    </td>
                    <td style={{ position: "relative", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        className="btn secondary small"
                        aria-label={t("th_actions")}
                        style={{ padding: "1px 10px", fontSize: 18, lineHeight: 1 }}
                        onClick={() => setMenuId(menuId === s.id ? null : s.id)}
                      >
                        &#8943;
                      </button>
                      {menuId === s.id && (
                        <>
                          <div onClick={() => setMenuId(null)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                          <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, zIndex: 41,
                            background: "#fff", border: "1px solid #e3e8ea", borderRadius: 10,
                            boxShadow: "0 6px 20px rgba(6,59,72,0.14)", minWidth: 190, padding: 6, textAlign: "left" }}>
                            {mItem(s.photo_url ? t("change_photo") : t("add_photo"), () => triggerPhoto(s))}
                            {!s.no_login && mItem(t("reset_password"), () => resetPassword(s))}
                            {!s.no_login && mItem(t("reset_code") + (s.has_pin ? " \u2713" : ""), () => resetCode(s))}
                            {!s.no_login && mItem(t("access_menu"), () => setAccessId(accessId === s.id ? null : s.id))}
                            {mItem(s.active ? t("disable") : t("enable"), () => toggleActive(s))}
                            <div style={{ height: 1, background: "#eef2f2", margin: "4px 0" }} />
                            {mItem(t("delete"), () => remove(s), true)}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                  {accessId === s.id && !s.no_login && (
                    <tr>
                      <td colSpan={stationsOn ? 8 : 7} style={{ background: "#f4f8f7" }}>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", padding: "8px 6px" }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>Data access:</span>
                          {PERMS.map(([k, label]) => (
                            <label key={k} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                              <input type="checkbox" checked={(s.effective_permissions || []).includes(k)} onChange={() => togglePerm(s, k)} /> {label}
                            </label>
                          ))}
                          <span className="muted" style={{ fontSize: 12 }}>Managers & controllers have all by default; untick to restrict.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
